import { useIsCountrySpecificEhrSelectionEnabled } from '@/features/flags';
import { personalizationSharedMessages } from '@/features/onboarding-v2/components/personalization/shared/personalization-shared.messages';
import { getEhrProviderMetadata } from '@/helpers/ehrProviderMetadata';
import { useUserCountryCode } from '@/hooks/query/useUserCountryCode';
import {
  EHR_OTHER_PROVIDER_ID,
  EhrPickerList as EhrPickerListView,
  type EhrPickerListProps,
  type PickedEhr,
} from '@heidi/agents';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useQueryIntegrationProviders } from '@heidi/integrations/ehr/data';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

// Code-owned option set: built once from the canonical EHRProvider enum
// so it stays in lockstep with the backend's supported providers. The
// agent never authors these — the id (sent back) is the raw enum value,
// while the label is the curated display name so clinicians see polished
// provider names ('Amazing Charts', not 'AmazingCharts').
//
// ``Heidi`` is the sentinel for "the app itself", not a connectable EHR
// (the integration selector in ehr-general-card.tsx filters it the same
// way); dropping it stops a pick resolving with Heidi as the target EHR.
const ENUM_EHR_OPTIONS: readonly PickedEhr[] = Object.values(EHRProvider)
  .filter((provider) => provider !== EHRProvider.Heidi)
  .map((provider) => ({
    id: provider,
    label: getEhrProviderMetadata(provider).displayName,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

/** Binds the EHR picker to the app's provider options. */
export const EhrPickerList = (props: EhrPickerListProps): JSX.Element => {
  const intl = useIntl();
  const isCountryScopedEhrList = useIsCountrySpecificEhrSelectionEnabled();
  const userCountryCode = useUserCountryCode();
  const providersQuery = useQueryIntegrationProviders({
    country: userCountryCode,
    enabled: isCountryScopedEhrList && !!userCountryCode,
  });

  const options: readonly PickedEhr[] = useMemo(() => {
    if (!isCountryScopedEhrList) return ENUM_EHR_OPTIONS;
    const otherOption: PickedEhr = {
      id: EHR_OTHER_PROVIDER_ID,
      label: intl.formatMessage(personalizationSharedMessages.other),
    };
    // Unlike the other flagged surfaces this picker has no country selector,
    // so with no resolvable country (or a failed fetch) "Other" must stay
    // pickable or the pick_ehr clarification becomes unanswerable.
    if (!userCountryCode || providersQuery.isError) return [otherOption];
    if (!providersQuery.isSuccess) return [];
    return [
      ...providersQuery.data.map((provider) => ({
        // Display name doubles as the id sent back in the pick_ehr answer.
        id: provider.name,
        label: provider.name,
      })),
      otherOption,
    ];
  }, [
    isCountryScopedEhrList,
    userCountryCode,
    providersQuery.isError,
    providersQuery.isSuccess,
    providersQuery.data,
    intl,
  ]);

  return (
    <>
      <EhrPickerListView {...props} options={options} />
      {isCountryScopedEhrList && providersQuery.isError && (
        <div className="flex items-center gap-1.5 pl-0.5">
          <TypographyCaption className="text-validation-destructive">
            {intl.formatMessage({
              defaultMessage: "We couldn't load the EHR list.",
              id: 'k2ef/l1mnS',
            })}
          </TypographyCaption>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => void providersQuery.refetch()}
          >
            {intl.formatMessage({ defaultMessage: 'Try again', id: 'FazwRldA7z' })}
          </Button>
        </div>
      )}
    </>
  );
};
